import { openDB } from 'idb';

const DATABASE_NAME = 'picsect-local-original-sources';
const DATABASE_VERSION = 1;
const SOURCE_STORE = 'sources';
const BINDING_STORE = 'bindings';

let databasePromise = null;

function normalizeId(value) {
  return String(value || '').trim();
}

function makeId(prefix) {
  const suffix = globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${suffix}`;
}

function bindingId(albumId, pictureId) {
  return `${normalizeId(albumId)}:${normalizeId(pictureId)}`;
}

function openLocalSourceDatabase() {
  if (databasePromise) return databasePromise;
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  databasePromise = openDB(DATABASE_NAME, DATABASE_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(SOURCE_STORE)) {
        const sources = database.createObjectStore(SOURCE_STORE, { keyPath: 'id' });
        sources.createIndex('albumId', 'albumId');
      }
      if (!database.objectStoreNames.contains(BINDING_STORE)) {
        const bindings = database.createObjectStore(BINDING_STORE, { keyPath: 'id' });
        bindings.createIndex('albumId', 'albumId');
      }
    },
  }).catch(error => {
    databasePromise = null;
    throw error;
  });
  return databasePromise;
}

function normalizeBindings(albumId, sourceId, bindings = []) {
  return bindings.map(binding => {
    const pictureId = normalizeId(binding.pictureId);
    const fileName = String(binding.fileName || '').trim();
    if (!pictureId || !fileName) return null;
    return {
      id: bindingId(albumId, pictureId),
      albumId,
      pictureId,
      sourceId,
      fileName,
      savedAt: Date.now(),
    };
  }).filter(Boolean);
}

async function saveDirectorySource({
  albumId,
  directoryHandle,
  bindings = [],
  oneClickEligible = false,
}) {
  const normalizedAlbumId = normalizeId(albumId);
  if (!normalizedAlbumId || !directoryHandle || bindings.length === 0) return null;
  const database = await openLocalSourceDatabase();
  if (!database) return null;

  const sourceId = makeId('directory');
  const transaction = database.transaction([SOURCE_STORE, BINDING_STORE], 'readwrite');
  transaction.objectStore(SOURCE_STORE).put({
    id: sourceId,
    albumId: normalizedAlbumId,
    kind: 'directory',
    handle: directoryHandle,
    oneClickEligible: oneClickEligible === true,
    savedAt: Date.now(),
  });
  normalizeBindings(normalizedAlbumId, sourceId, bindings).forEach(binding => {
    transaction.objectStore(BINDING_STORE).put(binding);
  });
  await transaction.done;
  return sourceId;
}

async function saveFileSources({ albumId, bindings = [], oneClickEligible = false }) {
  const normalizedAlbumId = normalizeId(albumId);
  const validBindings = bindings.filter(binding => (
    normalizeId(binding.pictureId)
    && String(binding.fileName || '').trim()
    && binding.fileHandle
  ));
  if (!normalizedAlbumId || validBindings.length === 0) return [];
  const database = await openLocalSourceDatabase();
  if (!database) return [];

  const transaction = database.transaction([SOURCE_STORE, BINDING_STORE], 'readwrite');
  const sourceIds = validBindings.map(binding => {
    const sourceId = makeId('file');
    transaction.objectStore(SOURCE_STORE).put({
      id: sourceId,
      albumId: normalizedAlbumId,
      kind: 'file',
      handle: binding.fileHandle,
      oneClickEligible: oneClickEligible === true,
      savedAt: Date.now(),
    });
    const [normalizedBinding] = normalizeBindings(normalizedAlbumId, sourceId, [binding]);
    transaction.objectStore(BINDING_STORE).put(normalizedBinding);
    return sourceId;
  });
  await transaction.done;
  return sourceIds;
}

async function loadOriginalSourcePlan({ albumId, pictures = [] }) {
  const normalizedAlbumId = normalizeId(albumId);
  const pictureIds = pictures.map(picture => normalizeId(picture?.id)).filter(Boolean);
  if (!normalizedAlbumId || pictureIds.length === 0) {
    return { complete: false, oneClickEligible: false, entries: [], missingPictureIds: pictureIds };
  }
  const database = await openLocalSourceDatabase();
  if (!database) {
    return { complete: false, oneClickEligible: false, entries: [], missingPictureIds: pictureIds };
  }

  const albumBindings = await database.getAllFromIndex(BINDING_STORE, 'albumId', normalizedAlbumId);
  const bindingByPictureId = new Map(albumBindings.map(binding => [binding.pictureId, binding]));
  const sourceIds = [...new Set(albumBindings.map(binding => binding.sourceId))];
  const sources = await Promise.all(sourceIds.map(sourceId => database.get(SOURCE_STORE, sourceId)));
  const sourceById = new Map(sources.filter(Boolean).map(source => [source.id, source]));
  const entries = [];
  const missingPictureIds = [];

  pictures.forEach(picture => {
    const pictureId = normalizeId(picture?.id);
    if (!pictureId) return;
    const binding = bindingByPictureId.get(pictureId);
    const source = binding ? sourceById.get(binding.sourceId) : null;
    if (!binding || !source?.handle) {
      missingPictureIds.push(pictureId);
      return;
    }
    entries.push({ picture, binding, source });
  });

  const complete = entries.length === pictureIds.length && missingPictureIds.length === 0;
  return {
    complete,
    oneClickEligible: complete && entries.every(entry => entry.source.oneClickEligible === true),
    entries,
    missingPictureIds,
  };
}

function uniqueSourceHandles(entries = []) {
  const seen = new Set();
  return entries.map(entry => entry.source).filter(source => {
    if (!source?.id || !source.handle || seen.has(source.id)) return false;
    seen.add(source.id);
    return true;
  });
}

async function requestOriginalSourcePermissions(entries = []) {
  const sources = uniqueSourceHandles(entries);
  const requests = sources.map(source => {
    if (typeof source.handle.requestPermission !== 'function') return Promise.resolve('granted');
    return source.handle.requestPermission({ mode: 'read' }).catch(() => 'denied');
  });
  const states = await Promise.all(requests);
  return states.every(state => state === 'granted');
}

async function getOriginalFile(entry) {
  if (!entry?.source?.handle) throw new Error('LOCAL_SOURCE_MISSING');
  if (entry.source.kind === 'file') return entry.source.handle.getFile();
  const fileHandle = await entry.source.handle.getFileHandle(entry.binding.fileName);
  return fileHandle.getFile();
}

async function clearAlbumOriginalSources(albumId) {
  const normalizedAlbumId = normalizeId(albumId);
  if (!normalizedAlbumId) return;
  const database = await openLocalSourceDatabase();
  if (!database) return;
  const [sources, bindings] = await Promise.all([
    database.getAllFromIndex(SOURCE_STORE, 'albumId', normalizedAlbumId),
    database.getAllFromIndex(BINDING_STORE, 'albumId', normalizedAlbumId),
  ]);
  const transaction = database.transaction([SOURCE_STORE, BINDING_STORE], 'readwrite');
  sources.forEach(source => transaction.objectStore(SOURCE_STORE).delete(source.id));
  bindings.forEach(binding => transaction.objectStore(BINDING_STORE).delete(binding.id));
  await transaction.done;
}

function resetLocalSourceDatabaseForTests() {
  databasePromise = null;
}

export {
  BINDING_STORE,
  DATABASE_NAME,
  SOURCE_STORE,
  clearAlbumOriginalSources,
  getOriginalFile,
  loadOriginalSourcePlan,
  requestOriginalSourcePermissions,
  resetLocalSourceDatabaseForTests,
  saveDirectorySource,
  saveFileSources,
  uniqueSourceHandles,
};
